'use client';

import { useState, useEffect } from 'react';
import SeverityBadge, { Severity } from '@/components/ui/SeverityBadge';

const initialEvents = [
  { id: 'EVT-009182', ts: '07:18:29', src: '185.220.101.47', dst: '10.0.1.15', type: 'Brute Force SSH', severity: 'critical' as Severity, rule: 'SSH-BF-001', asset: 'auth-server-01' },
  { id: 'EVT-009181', ts: '07:18:21', src: '45.133.1.57', dst: '10.0.4.22', type: 'SQL Injection', severity: 'high' as Severity, rule: 'SQLI-WEB-004', asset: 'webapp-prod-02' },
  { id: 'EVT-009180', ts: '07:18:14', src: '103.75.246.18', dst: '10.0.2.8', type: 'Port Scan — Full', severity: 'medium' as Severity, rule: 'SCAN-PORT-002', asset: 'dmz-fw-01' },
  { id: 'EVT-009179', ts: '07:17:58', src: '91.108.4.23', dst: '10.0.1.5', type: 'DNS Tunneling', severity: 'critical' as Severity, rule: 'DNS-TUNNEL-001', asset: 'core-dns-01' },
  { id: 'EVT-009178', ts: '07:17:44', src: '10.0.8.113', dst: '203.0.113.42', type: 'C2 Beacon', severity: 'critical' as Severity, rule: 'C2-BEACON-007', asset: 'workstation-hr-14' },
  { id: 'EVT-009177', ts: '07:17:31', src: '198.51.100.8', dst: '10.0.3.17', type: 'XSS Attempt', severity: 'medium' as Severity, rule: 'XSS-WEB-012', asset: 'webapp-prod-01' },
  { id: 'EVT-009176', ts: '07:17:19', src: '77.88.55.80', dst: '10.0.1.15', type: 'Auth Failure ×43', severity: 'high' as Severity, rule: 'AUTH-FAIL-003', asset: 'auth-server-01' },
  { id: 'EVT-009175', ts: '07:17:02', src: '10.0.5.201', dst: '10.0.1.1', type: 'Lateral Movement', severity: 'critical' as Severity, rule: 'LATERAL-003', asset: 'workstation-dev-07' },
  { id: 'EVT-009174', ts: '07:16:48', src: '192.0.2.14', dst: '10.0.6.9', type: 'RDP Brute Force', severity: 'high' as Severity, rule: 'RDP-BF-002', asset: 'rdp-gateway-01' },
  { id: 'EVT-009173', ts: '07:16:33', src: '185.220.101.12', dst: '10.0.4.22', type: 'LFI Attempt', severity: 'medium' as Severity, rule: 'LFI-WEB-006', asset: 'webapp-prod-02' },
];

export default function LiveEventFeed() {
  const [events, setEvents] = useState(initialEvents);
  const [newEventIds, setNewEventIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Backend integration: Replace with WebSocket subscription to event stream
    const interval = setInterval(() => {
      const nextId = `EVT-${(9183 + Math.floor(Math.random() * 10)).toString().padStart(6, '0')}`;
      const severities: Severity[] = ['critical', 'high', 'medium', 'critical', 'high'];
      const types = ['Brute Force SSH', 'SQL Injection', 'Port Scan', 'DNS Query Anomaly', 'Auth Failure'];
      const newEvt = {
        id: nextId,
        ts: new Date().toLocaleTimeString('en-US', { hour12: false, timeZone: 'UTC' }),
        src: `${Math.floor(Math.random() * 200 + 50)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
        dst: `10.0.${Math.floor(Math.random() * 8 + 1)}.${Math.floor(Math.random() * 254 + 1)}`,
        type: types[Math.floor(Math.random() * types.length)],
        severity: severities[Math.floor(Math.random() * severities.length)],
        rule: `RULE-${Math.floor(Math.random() * 999).toString().padStart(3, '0')}`,
        asset: `asset-${Math.floor(Math.random() * 20 + 1).toString().padStart(2, '0')}`,
      };
      setEvents((prev) => [newEvt, ...prev.slice(0, 9)]);
      setNewEventIds((prev) => new Set([...prev, newEvt.id]));
      setTimeout(() => setNewEventIds((prev) => { const s = new Set(prev); s.delete(newEvt.id); return s; }), 1500);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl flex flex-col">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-800">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500"></span>
          </span>
          <h3 className="text-sm font-semibold text-zinc-200">Canlı Kritik Olaylar</h3>
          <span className="text-xs text-zinc-600 font-mono">— gerçek zamanlı akış</span>
        </div>
        <a href="/event-monitoring" className="text-xs text-rose-400 hover:text-rose-300 transition-colors font-medium">
          Tümünü gör →
        </a>
      </div>

      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-zinc-800/60">
              {['Olay ID', 'Saat (UTC)', 'Kaynak IP', 'Hedef IP', 'Tür', 'Önem', 'Varlık', 'Kural'].map((h) => (
                <th key={h} className="px-3 py-2 text-left font-medium text-zinc-600 uppercase tracking-widest text-[10px] whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {events.map((evt) => (
              <tr
                key={evt.id}
                className={`
                  border-b border-zinc-800/40 hover:bg-zinc-800/50 transition-all duration-300 cursor-pointer
                  ${newEventIds.has(evt.id) ? 'bg-rose-500/8 animate-fade-in' : ''}
                `}
              >
                <td className="px-3 py-2 font-mono text-zinc-400 whitespace-nowrap">{evt.id}</td>
                <td className="px-3 py-2 font-mono text-zinc-500 whitespace-nowrap">{evt.ts}</td>
                <td className="px-3 py-2 font-mono text-amber-400/90 whitespace-nowrap">{evt.src}</td>
                <td className="px-3 py-2 font-mono text-blue-400/80 whitespace-nowrap">{evt.dst}</td>
                <td className="px-3 py-2 text-zinc-300 whitespace-nowrap">{evt.type}</td>
                <td className="px-3 py-2 whitespace-nowrap"><SeverityBadge severity={evt.severity} /></td>
                <td className="px-3 py-2 font-mono text-zinc-500 whitespace-nowrap">{evt.asset}</td>
                <td className="px-3 py-2 font-mono text-zinc-600 whitespace-nowrap">{evt.rule}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}